import { currentLang } from "i18n";
import { localizeMonthNames } from "shared/utils/dayjs";

// The response fields the server fills with a date written in English (see
// localizeMonthNames). Only these are rewritten: a product, class or line
// item that happens to be called "May Promo" must never change.
const SERVER_DATE_LABEL_KEYS = new Set([
  "range_label", // every Analitik report: "1 Jan 2026–5 Oct 2026"
  "month", // Ringkasan Pendapatan chart keys: "Oct"
  "month_label", // Ringkasan Pendapatan rows: "Oct 2026"
  "current_label", // Home KPI captions: "Oct 2026"
  "previous_label",
  "current_range", // Home comparison: "1–5 Oct 2026"
  "previous_range",
  "closingDateLabel", // Payroll closing dates: "30 Sep 2026"
  "periodLabel",
  "leftover_label", // Payroll days a period leaves unpaid: "1 Agt–9 Agt 2026"
  "paid_label", // Payroll days an earlier run already covers: "14 Sep 2026"
]);

const walk = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(walk);
  if (!value || typeof value !== "object" || value instanceof Date) return value;
  const out: Record<string, unknown> = {};
  for (const [key, field] of Object.entries(value)) {
    out[key] =
      typeof field === "string" && SERVER_DATE_LABEL_KEYS.has(key)
        ? localizeMonthNames(field)
        : walk(field);
  }
  return out;
};

/**
 * Puts the month names of a response's server-written date labels into the
 * admin's language. Registered as the `select` default of the queries that
 * carry such labels (index.js), so every page, chart and PDF built from them
 * gets it, including hooks added later; a plain fetch outside react-query
 * (fetchAnalyticsRows) calls it itself. Runs at read time, not fetch time, so
 * switching the language relabels the data already in the cache.
 */
export const localizeServerDateLabels = <T,>(data: T): T =>
  currentLang() === "en" ? data : (walk(data) as T);
